/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon } from 'semantic-ui-react';

import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreEmpty, isStoreError, isStoreLoading } from '@amzn/base-ui/dist/models/BaseStore';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import CreateInternalEnvForm from './CreateInternalEnvForm';
import { loadVolumeSizeRanges, volumeSizeRangesLoaded } from './EnvTypeAdditionalInfo';
import { isDeprecatedName } from '../../../helpers/env-type-display';

// expected props
// - onPrevious (via props)
// - onCompleted (via props) a function is called after a call to create a workspace is performed
// - envTypeId (via props)
// - studyIds (via props)
// - envTypesStore (via injection)
// - clientInformationStore (via injection)
// - userStore (via injection)
// - scEnvironmentsStore (via injection)
class ConfigureEnvTypeStep extends React.Component {
  componentDidMount() {
    window.scrollTo(0, 0);

    swallowError(this.envTypeConfigsStore.load());
    swallowError(loadVolumeSizeRanges());
  }

  get envTypeId() {
    return this.props.envTypeId;
  }

  get rootVolume() {
    return this.props.rootVolume;
  }

  get hyperthreading() {
    return this.props.enableHyperthreading;
  }

  get envTypeTitle() {
    return _.get(this.envTypesStore.getEnvType(this.envTypeId), 'name');
  }

  get userStore() {
    return this.props.userStore;
  }

  get clientInformationStore() {
    return this.props.clientInformationStore;
  }

  get envTypesStore() {
    return this.props.envTypesStore;
  }

  get scEnvironmentsStore() {
    return this.props.scEnvironmentsStore;
  }

  get envTypeConfigsStore() {
    return this.envTypesStore.getEnvTypeConfigsStore(this.envTypeId);
  }

  // Configs named with the DEPRECATED marker (the same retirement convention used for workspace
  // types) are hidden from researchers, mirroring the filter SelectEnvTypeStep applies for types.
  // Admins still see them, unless previewing "Researcher View".
  get hideDeprecated() {
    const isAdmin = _.get(this.userStore, 'user.isAdmin', false);
    const researcherView = _.get(this.userStore, 'researcherView', false);
    return !isAdmin || researcherView;
  }

  get configurations() {
    const list = this.envTypeConfigsStore.list;
    if (!this.hideDeprecated) return list;
    return _.filter(list, config => !isDeprecatedName(config.name));
  }

  get defaultCidr() {
    // Only show the CIDR field if the env type's CFN template declares an AccessFromCIDRBlock parameter
    const envType = this.envTypesStore.getEnvType(this.envTypeId);
    const hasCidrParam = _.some(_.get(envType, 'params', []), (p) => p.ParameterKey === 'AccessFromCIDRBlock');
    if (!hasCidrParam) return undefined;
    if (_.isEmpty(this.clientInformationStore.ipAddress)) return '';

    return `${this.clientInformationStore.ipAddress}/32`;
  }

  get studyIds() {
    return this.props.studyIds;
  }

  handlePrevious = () => {
    if (_.isFunction(this.props.onPrevious)) this.props.onPrevious();
  };

  // eslint-disable-next-line consistent-return
  handleCreate = async data => {
    const studyIds = this.studyIds || [];
    const store = this.scEnvironmentsStore;
    const environment = await store.createScEnvironment({ ...data, studyIds });
    return this.props.onCompleted(environment);
  };

  render() {
    const store = this.envTypeConfigsStore;
    let content = null;

    if (isStoreError(store)) {
      content = this.renderLoadingError();
    } else if (isStoreLoading(store) || !volumeSizeRangesLoaded()) {
      // Wait for the volume size ranges before rendering the form. CreateInternalEnvForm reads
      // the range for this workspace type synchronously in its constructor, to seed the root
      // volume field's value and its min/max validation rules, and it only does so once. If it
      // is constructed before the ranges have arrived it uses the generic fallback range and
      // keeps it, which let users pick a volume below the minimum for types whose minimum is
      // above the fallback's.
      //
      // The check has to cover the store's "initial" state as well as "loading": the store is
      // created lazily and is only asked to load in componentDidMount, which React runs after
      // the first render, so on first entry to this step the form would otherwise be built
      // before loadVolumeSizeRanges had even been called.
      //
      // volumeSizeRangesLoaded reads an observable and this component is an observer, so the
      // re-render happens on its own once the fetch lands. It resolves either way, the loader
      // falls back to defaults on failure rather than leaving this pending.
      content = <BasicProgressPlaceholder className="mt2" />;
    } else if (isStoreEmpty(store) || _.isEmpty(this.configurations)) {
      // The store itself may have configs, but they can all be deprecated and filtered out above
      // for the current viewer, in which case this looks the same as having no configs at all.
      content = this.renderEmpty();
    } else {
      content = this.renderContent();
    }

    return content;
  }

  renderLoadingError() {
    const store = this.envTypeConfigsStore;
    return (
      <>
        {this.renderButtons()}
        <ErrorBox error={store.error} className="p0 mt2 mb3" />
      </>
    );
  }

  renderContent() {
    const envTypeId = this.envTypeId;
    const configurations = this.configurations;
    const title = this.envTypeTitle;
    const defaultCidr = this.defaultCidr;
    const rootVolume = this.rootVolume;
    const enableHyperthreading = this.hyperthreading;
    const isExternal = this.userStore.user.isExternalUser;

    return (
      <div className="animated fadeIn">
        {!isExternal && (
          <CreateInternalEnvForm
            envTypeId={envTypeId}
            configurations={configurations}
            title={title}
            defaultCidr={defaultCidr}
            rootVolume={rootVolume}
            enableHyperthreading={enableHyperthreading}
            onPrevious={this.handlePrevious}
            onNext={this.handleCreate}
            onCancel={this.props.onCancel}
          />
        )}
        {isExternal && this.renderExternalNotSupported()}
      </div>
    );
  }

  renderExternalNotSupported() {
    return (
      <>
        {this.renderButtons()}
        <div className="workspace-empty-state mt2">
          <div className="workspace-empty-state-icon workspace-empty-state-icon-warning">
            <Icon name="server" />
          </div>
          <div className="workspace-empty-state-title">No support for external researchers</div>
          <div className="workspace-empty-state-subtitle">
            There are no workspace configurations to choose from. Your role is restricted. Please contact your
            administrator.
          </div>
        </div>
      </>
    );
  }

  renderEmpty() {
    return (
      <>
        {this.renderButtons()}
        <div className="workspace-empty-state mt2">
          <div className="workspace-empty-state-icon">
            <Icon name="server" />
          </div>
          <div className="workspace-empty-state-title">No workspace configurations</div>
          <div className="workspace-empty-state-subtitle">
            There are no workspace configurations to choose from. Your role might be restricted. Please contact your
            administrator.
          </div>
        </div>
      </>
    );
  }

  renderButtons({ nextDisabled = true } = {}) {
    return (
      <div className="setup-top-bar">
        <div className="setup-top-bar-left">
          <span className="setup-top-bar-title">
            <Icon name="hdd outline" /> Configure Workspace
          </span>
        </div>
        <div className="setup-top-bar-nav">
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            onClick={this.handlePrevious}
          >
            <Icon name="arrow left" /> Back
          </button>
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-primary"
            disabled={nextDisabled}
          >
            Create Workspace <Icon name="arrow right" />
          </button>
        </div>
        <div className="setup-top-bar-description">
          Name your workspace, choose your hardware configuration and create it.
        </div>
      </div>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ConfigureEnvTypeStep, {
  handlePrevious: action,
  handleCreate: action,
  userStore: computed,
  envTypesStore: computed,
  clientInformationStore: computed,
  envTypeId: computed,
  rootVolume: computed,
  hyperthreading: computed,
  defaultCidr: computed,
  envTypeTitle: computed,
  scEnvironmentsStore: computed,
  configurations: computed,
  hideDeprecated: computed,
  studyIds: computed,
});

export default inject(
  'userStore',
  'envTypesStore',
  'scEnvironmentsStore',
  'clientInformationStore',
)(observer(ConfigureEnvTypeStep));
